/**
 * Dynamic-form input formats (QuestionBase.inputFormat on the server).
 * Clients SHOW formatted text and STORE the plain value; the server cleans
 * and validates the same way (nadlan/services/validators.py
 * normalize_formatted_answers / format_error). Same rules as the app
 * (lib/utils/field_format.dart).
 *
 *   price         1,250,000 ₪      -> 1250000
 *   km / area     85,000 ק"מ       -> 85000
 *   integer       1,200 (may be -) -> 1200
 *   licensePlate  12-345-67 / 123-45-678 -> digits
 *   phone         050-1234567      -> digits
 *   date / email / text            -> as typed (date: native picker)
 */
export type InputFormat =
  'text' | 'price' | 'integer' | 'decimal' | 'km' | 'area' | 'licensePlate' | 'date' | 'phone' | 'email';

const KNOWN = new Set<InputFormat>(['text', 'price', 'integer', 'decimal', 'km', 'area', 'licensePlate', 'date', 'phone', 'email']);

export function inputFormatOf(question: { inputFormat?: string | null; type?: string | null } | null | undefined): InputFormat {
  const fmt = String(question?.inputFormat || '').trim() as InputFormat;
  if (KNOWN.has(fmt)) return fmt;
  const type = String(question?.type || '').toLowerCase();
  if (['number', 'int', 'integer'].includes(type)) return 'integer';
  if (['decimal', 'float', 'double'].includes(type)) return 'decimal';
  if (type === 'date') return 'date';
  if (type === 'email') return 'email';
  if (type === 'phone' || type === 'tel') return 'phone';
  return 'text';
}

/** Formats typed through a masked text input (the rest use native inputs). */
export function isMaskedFormat(fmt: InputFormat): boolean {
  return ['price', 'integer', 'decimal', 'km', 'area', 'licensePlate', 'phone'].includes(fmt);
}

/** i18n key of the unit shown next to the value, or null. */
export function unitKey(fmt: InputFormat): string | null {
  switch (fmt) {
    case 'price': return 'sale4you.label.format.currency';
    case 'km': return 'sale4you.label.format.km';
    case 'area': return 'sale4you.label.format.area';
    default: return null;
  }
}

/** The plain value to store for what the user typed. */
export function cleanValue(fmt: InputFormat, text: any): string {
  const value = String(text ?? '');
  switch (fmt) {
    case 'price': case 'km': case 'area':
      return value.replace(/\D/g, '');
    case 'integer': {
      const digits = value.replace(/\D/g, '');
      return value.trim().startsWith('-') && digits ? '-' + digits : digits;
    }
    case 'decimal': {
      const negative = value.trim().startsWith('-');
      const [whole, ...rest] = value.replace(/[^\d.]/g, '').split('.');
      const cleaned = rest.length ? `${whole}.${rest.join('')}` : whole;
      return negative && cleaned ? '-' + cleaned : cleaned;
    }
    case 'licensePlate':
      return value.replace(/\D/g, '').slice(0, 8);
    case 'phone':
      return value.replace(/\D/g, '').slice(0, 10);
    default:
      return value;
  }
}

function groupThousands(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** What the field shows for a plain stored value. */
export function displayValue(fmt: InputFormat, raw: any): string {
  const value = cleanValue(fmt, raw);
  if (!value) return '';
  switch (fmt) {
    case 'price': case 'km': case 'area':
      return groupThousands(value);
    case 'integer':
      return value.startsWith('-') ? '-' + groupThousands(value.slice(1)) : groupThousands(value);
    case 'decimal': {
      const negative = value.startsWith('-');
      const [whole, fraction] = value.replace('-', '').split('.');
      return (negative ? '-' : '') + groupThousands(whole) + (fraction !== undefined ? '.' + fraction : '');
    }
    case 'licensePlate':
      // Israeli plates: 7 digits 12-345-67, 8 digits 123-45-678.
      if (value.length === 8) return `${value.slice(0, 3)}-${value.slice(3, 5)}-${value.slice(5)}`;
      return [value.slice(0, 2), value.slice(2, 5), value.slice(5, 7)].filter(Boolean).join('-');
    case 'phone':
      return value.length > 3 ? `${value.slice(0, 3)}-${value.slice(3)}` : value;
    default:
      return value;
  }
}

/** Mobile keyboard for the format. */
export function inputModeOf(fmt: InputFormat): string {
  switch (fmt) {
    case 'price': case 'km': case 'area': case 'licensePlate': return 'numeric';
    case 'integer': return 'numeric';
    case 'decimal': return 'decimal';
    case 'phone': return 'tel';
    case 'email': return 'email';
    default: return 'text';
  }
}
